import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Eye, Trash2, Crown, Phone, MapPin, Calendar, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Modal } from "@/components/admin/ui";

const STATUSES = ["All", "Pending", "Approved", "Rejected"];
const statusStyle = {
  Pending: "bg-amber-500/15 text-amber-400",
  Approved: "bg-whatsapp/15 text-whatsapp",
  Rejected: "bg-red-500/15 text-red-400",
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="border-b border-gold/10 py-2">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-200">{value}</p>
    </div>
  );
}

export default function ManageHostApplications() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [view, setView] = useState(null);

  const { data: items } = useQuery({
    queryKey: ["admin-host-applications"],
    queryFn: async () => (await api.get("/admin/host-applications")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-host-applications"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => api.put(`/admin/host-applications/${id}/status`, { status }),
    onSuccess: (_, v) => { invalidate(); setView((cur) => (cur ? { ...cur, status: v.status } : cur)); toast.success(`Marked ${v.status}`); },
    onError: () => toast.error("Failed to update status"),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/host-applications/${id}`),
    onSuccess: () => { invalidate(); setView(null); toast.success("Application deleted"); },
  });

  const filtered = (items || []).filter((a) => {
    const matchStatus = filter === "All" || a.status === filter;
    const s = q.toLowerCase();
    const matchQ = !s || [a.full_name, a.user_id, a.whatsapp, a.country, a.city].some((v) => (v || "").toLowerCase().includes(s));
    return matchStatus && matchQ;
  });

  return (
    <div>
      <div>
        <h1 className="font-display text-3xl font-bold text-white">Host Applications</h1>
        <p className="mt-1 text-slate-400">Review and manage Official Host applications submitted from the website.</p>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID, number, country..."
            data-testid="host-app-search"
            className="h-11 w-full rounded-lg border border-gold/20 bg-obsidian pl-11 pr-3 text-slate-100 outline-none focus:border-gold/60" />
        </div>
        <div className="flex gap-2">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`host-app-filter-${s.toLowerCase()}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                filter === s ? "gold-gradient text-obsidian" : "border border-gold/25 text-slate-300 hover:border-gold/60"
              }`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {(filtered || []).map((a) => (
          <div key={a.id} className="card-surface flex items-center gap-4 rounded-xl p-4" data-testid={`host-app-${a.id}`}>
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/10">
              <Crown className="h-5 w-5 text-gold" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-bold text-slate-100">{a.full_name}</p>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyle[a.status] || ""}`}>{a.status}</span>
              </div>
              <p className="truncate text-sm text-slate-400">
                ID {a.user_id} · {a.whatsapp} · {[a.city, a.country].filter(Boolean).join(", ")}
              </p>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Calendar className="h-3 w-3" /> {fmt(a.created_at)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button onClick={() => setView(a)} className="rounded-lg p-2 text-gold hover:bg-gold/10" data-testid={`view-host-app-${a.id}`}><Eye className="h-5 w-5" /></button>
              <button onClick={() => { if (window.confirm("Delete this application?")) del.mutate(a.id); }} className="rounded-lg p-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-host-app-${a.id}`}><Trash2 className="h-5 w-5" /></button>
            </div>
          </div>
        ))}
        {filtered?.length === 0 && <p className="py-12 text-center text-slate-500" data-testid="host-app-empty">No applications found.</p>}
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title="Application Details" testid="host-app-detail-modal">
        {view && (
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-sm font-semibold ${statusStyle[view.status] || ""}`}>{view.status}</span>
              <span className="text-xs text-slate-500">Submitted {fmt(view.created_at)}</span>
            </div>

            <div className="max-h-[45vh] overflow-y-auto pr-1">
              <Row label="Full Name" value={view.full_name} />
              <Row label="Golden Live User ID" value={view.user_id} />
              <Row label="Mobile / WhatsApp" value={view.whatsapp} />
              <Row label="Age" value={view.age} />
              <Row label="Country" value={view.country} />
              <Row label="City" value={view.city} />
              <Row label="Current Agency" value={view.agency_name} />
              <Row label="Avg Daily Live Hours" value={view.avg_daily_hours} />
              <Row label="Previous Live App Experience" value={view.live_experience} />
              <Row label="Previous Host Experience" value={view.host_experience} />
              <Row label="Why become a Host" value={view.reason} />
              <Row label="Preferred Contact" value={view.preferred_contact} />
              <Row label="Additional Info" value={view.additional_info} />
            </div>

            <div className="mt-5 flex flex-wrap gap-2 border-t border-gold/10 pt-4">
              {view.whatsapp && (
                <a href={`https://wa.me/${view.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-whatsapp px-4 text-sm font-bold text-white hover:bg-whatsapp-hover">
                  <Phone className="h-4 w-4" /> WhatsApp
                </a>
              )}
              <div className="ml-auto flex gap-2">
                <button onClick={() => setStatus.mutate({ id: view.id, status: "Approved" })} data-testid="host-app-approve-btn"
                  className="inline-flex h-10 items-center rounded-lg bg-whatsapp/15 px-4 text-sm font-bold text-whatsapp hover:bg-whatsapp/25">Approve</button>
                <button onClick={() => setStatus.mutate({ id: view.id, status: "Rejected" })} data-testid="host-app-reject-btn"
                  className="inline-flex h-10 items-center rounded-lg bg-red-500/15 px-4 text-sm font-bold text-red-400 hover:bg-red-500/25">Reject</button>
                <button onClick={() => setStatus.mutate({ id: view.id, status: "Pending" })} data-testid="host-app-pending-btn"
                  className="inline-flex h-10 items-center rounded-lg bg-amber-500/15 px-4 text-sm font-bold text-amber-400 hover:bg-amber-500/25">Pending</button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
